import { FC } from 'react';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import type { SiteLastIndexedInfo } from '@/services/data-sources/get-wordpress-sites-last-indexed';
import { wpSiteKey } from '@/utils/wp-site-key';

interface WpSiteLastIndexedProps {
  baseUrl: string;
  sitesLastIndexed: Record<string, SiteLastIndexedInfo>;
  variant: 'single' | 'multi';
}

const getSiteInfo = (
  baseUrl: string,
  sitesLastIndexed: Record<string, SiteLastIndexedInfo>,
): SiteLastIndexedInfo | null => {
  const key = wpSiteKey(baseUrl);
  if (!key) return null;
  return sitesLastIndexed[key] ?? null;
};

export const WpSiteLastIndexed: FC<WpSiteLastIndexedProps> = ({
  baseUrl,
  sitesLastIndexed,
  variant,
}) => {
  const info = getSiteInfo(baseUrl, sitesLastIndexed);
  if (!info) return null;

  const hasError = Boolean(info.error);
  const hasTime = Boolean(info.last_index_time);
  const date = hasTime ? new Date(info.last_index_time as string) : null;

  if (variant === 'single') {
    return (
      <>
        <div>
          <span className='font-medium text-foreground'>Last indexed:</span>{' '}
          {hasError ? (
            <span className='text-destructive'>Error</span>
          ) : date ? (
            date.toLocaleString()
          ) : (
            'Never'
          )}
        </div>
        {info.point_count != null && (
          <div>
            <span className='font-medium text-foreground'>Points:</span>{' '}
            {info.point_count.toLocaleString()}
          </div>
        )}
      </>
    );
  }

  return (
    <>
      {hasError ? (
        <span className='text-destructive'>Last indexed: Error</span>
      ) : !date ? (
        <span>Last indexed: Never</span>
      ) : (
        <Tooltip>
          <TooltipTrigger asChild>
            <span>Last indexed: {date.toLocaleString()}</span>
          </TooltipTrigger>
          <TooltipContent>
            <p>{date.toLocaleString()}</p>
            {info.last_title && (
              <p className='text-xs opacity-80'>{info.last_title}</p>
            )}
          </TooltipContent>
        </Tooltip>
      )}
      {info.point_count != null && (
        <span>Points: {info.point_count.toLocaleString()}</span>
      )}
    </>
  );
};
